import { LAYER_NAMES } from '@App/app/entities/layer/enums/layer-names.enum';
import { LAYER_EVENTS, LAYER_TYPES } from '@App/app/entities/layer/enums/layer-types.enum';
import { SAVE_SKETCH_TYPE } from '@App/app/entities/layer/sketch-tools/save-sketch-types.enum';
import { EVENT_TYPE } from '@App/app/entities/shared/event-types.enum';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { filter, pluck, take } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '@App/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { addLayerSuccess } from '../store/actions/layers.actions';
import type { CreateLayersBarComponent } from './create-layers-bar.component';

/**
 * Wires up the broadcast/store/service subscriptions that drive the create-layers bar.
 * Extracted from the component constructor to keep the component focused on view logic.
 */
export const initCreateLayersBarSubscriptions = (c: CreateLayersBarComponent): void => {
  c.activeLayerToolService.activeTool$.pipe(untilDestroyed(c)).subscribe((activeTool) => {
    c.activeTool = activeTool;
    if (activeTool === LAYER_NAMES.CUBOID_PICKER) {
      c.activeTool = LAYER_NAMES.BOUNDING_BOX;
    }
  });

  c.layerEventsService.activeLayerEvent.pipe(untilDestroyed(c)).subscribe((value) => {
    c.activeEvent = value;
  });

  c.createLayerBarService.isEditMode$.subscribe((editMode) => {
    c.isEditMode = editMode;
  });

  c.actions$
    .pipe(
      ofType(addLayerSuccess),
      filter(({ layer }) => layer.type === LAYER_TYPES.BASE_PLANE && layer.isSaved),
      untilDestroyed(c),
    )
    .subscribe(() => {
      c.rendersService.currentRender$.pipe(take(1)).subscribe((render) => {
        if (render?.automapping) {
          c.dialogService
            .open(ConfirmationDialogComponent, {
              context: {
                title:
                  'There are some autogenerated cuboids for this model. Would you like to import them now?',
                status: 'info',
              },
            })
            .onClose.pipe(untilDestroyed(c))
            .subscribe((value) => {
              if (value && render.automapping) {
                c.automappingService.importMeshes(render.automapping?.downloadURL);
              }
            });
        }
      });
    });

  c.broadcastService
    .on(EVENT_TYPE.CHANGE_BOUNDING_BOX_BUTTON_STATUS)
    .pipe(untilDestroyed(c))
    .subscribe(() => {
      c.activeLayerToolService.setActiveTool(LAYER_NAMES.CUBOID_PICKER);
      c.layerEventsService.activeLayerEvent.next(LAYER_EVENTS.NULL);
    });

  c.editInitTools.forEach((tool) => {
    c.generateEditMode(tool);
  });

  c.broadcastService
    .on(EVENT_TYPE.FINISH_SKETCH_PLANE)
    .pipe(untilDestroyed(c))
    .subscribe(() => {
      c.createLayerBarService.toggleActiveLayer(
        LAYER_EVENTS.ADD_SKETCH_PLANE,
        LAYER_NAMES.SKETCH_PLANE,
        c.activeTool,
      );
    });

  c.broadcastService
    .on(EVENT_TYPE.ACTIVATE_SAVE_BUTTON)
    .pipe(untilDestroyed(c))
    .subscribe((name: SAVE_SKETCH_TYPE) => {
      c.sketchToSave = name;
      c.buttonsDisabled = false;
    });

  c.broadcastService
    .on(EVENT_TYPE.REMOVE_ACTIVE_LAYER_BUTTON)
    .pipe(untilDestroyed(c))
    .subscribe(() => {
      c.activeLayerToolService.setActiveTool();
    });

  c.isPlaneEditing$ = c.broadcastService
    .on(EVENT_TYPE.EDIT_SKETCHES)
    .pipe(untilDestroyed(c), pluck('isAddPlaneActive'));
  c.hintsActive$ = c.hintsService.hintsActive$;
  c.currentHint$ = c.hintsService.currentHint$;

  c.layerEventsService.activeLayerEvent.pipe(untilDestroyed(c)).subscribe(() => {
    c.hidePopover();
  });
};
